import { SetStateAction, useCallback, useEffect, useRef, useState } from 'react';

type Callback<T> = (state: T) => void;
type SetStateWithCallback<T> = (updatedState: SetStateAction<T>, callback?: Callback<T>) => void;

export const useStateWithCallback = <T>(
  initialState: T | (() => T),
): [state: T, setState: SetStateWithCallback<T>] => {
  const [state, setState] = useState<T>(initialState);
  const callbackRef = useRef<Callback<T>>();
  const isMounted = useRef(true);

  const handleSetStateWithCallback: SetStateWithCallback<T> = useCallback(
    (updatedState, callback) => {
      callbackRef.current = callback;
      setState(updatedState);
    },
    [],
  );

  useEffect(() => {
    if (isMounted.current) {
      isMounted.current = false;
      return;
    }
    if (typeof callbackRef.current === 'function') {
      callbackRef.current(state);
      callbackRef.current = undefined;
    }
  }, [state]);

  return [state, handleSetStateWithCallback];
};
